/**
 * Location types for bookings.
 * Broad categories for different booking contexts.
 */
export const LOCATION_TYPES = [
    'property', // Physical property (hotel, resort, vacation rental)
    'service', // Service-based booking (spa, salon, restaurant)
    'event', // Event/activity booking (concert, tour, class)
    'experience', // Experience package (multi-activity, bundle)
    'destination', // Destination/location-based (city pass, area access)
];
/**
 * Check if a booking status satisfies a requirement type.
 *
 * @param status - Current booking status ('past', 'active', 'future')
 * @param requirement - Required booking status for eligibility
 * @returns true if the status satisfies the requirement
 *
 * @example
 * isBookingStatusValid('active', 'active_future'); // true
 * isBookingStatusValid('past', 'active_future');   // false
 * isBookingStatusValid('future', 'any');           // true
 */
export function isBookingStatusValid(status, requirement) {
    switch (requirement) {
        case 'active':
            return status === 'active';
        case 'future':
            return status === 'future';
        case 'past':
            return status === 'past';
        case 'active_future':
            return status === 'active' || status === 'future';
        case 'any':
            return true;
        default:
            return false;
    }
}
/**
 * Compute booking status based on check-in and check-out dates.
 *
 * Logic:
 * - 'past': Check-out date is before today (booking ended yesterday or earlier)
 * - 'active': Check-in date is today or earlier, AND check-out is today or later (guest is staying)
 * - 'future': Check-in date is after today (upcoming booking)
 *
 * Note: Checkout day itself counts as 'active' since guest is there until end of day.
 *
 * @param checkInDate - Booking start date
 * @param checkOutDate - Booking end date
 * @param referenceDate - Date to compare against (defaults to current date)
 * @returns Computed booking status
 * @throws Error if checkOutDate is not after checkInDate
 *
 * @example
 * // Today is June 5
 * computeBookingStatus('2026-06-01', '2026-06-04') // 'past' (checked out yesterday)
 * computeBookingStatus('2026-06-01', '2026-06-05') // 'active' (checking out today)
 * computeBookingStatus('2026-06-03', '2026-06-08') // 'active' (currently staying)
 * computeBookingStatus('2026-06-10', '2026-06-15') // 'future' (upcoming)
 */
export function computeBookingStatus(checkInDate, checkOutDate, referenceDate = new Date()) {
    // Normalize all dates to start of day (00:00:00)
    const now = new Date(referenceDate);
    const checkIn = new Date(checkInDate);
    const checkOut = new Date(checkOutDate);
    now.setHours(0, 0, 0, 0);
    checkIn.setHours(0, 0, 0, 0);
    checkOut.setHours(0, 0, 0, 0);
    // Defensive validation: checkOut must be after checkIn
    // This catches invalid data from frontend or data integrity issues
    if (checkOut <= checkIn) {
        throw new Error(`Invalid booking dates: checkOut (${checkOut.toISOString()}) must be after checkIn (${checkIn.toISOString()})`);
    }
    // Checkout day itself counts as still active (guest there until end of day)
    if (checkOut < now) {
        return 'past';
    }
    else if (checkIn <= now) {
        // Note: checkOut >= now is implicit here (already ruled out checkOut < now above)
        return 'active';
    }
    else {
        return 'future';
    }
}
//# sourceMappingURL=booking.type.js.map